import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';
import { environment } from 'src/environment/environment';
import {
  MetadataDataFormat,
  MetadataField,
  MetadataTranslation,
  MetadataUpsertModel,
} from '../models/metadata.model';

interface ExtractionMetadataTranslationDto {
  languageId: number;
  text: string;
}

interface ExtractionMetadataDto {
  id: string;
  dataFormat: number;
  extractionQuestion: string;
  displayName: string;
  translations: ExtractionMetadataTranslationDto[];
  documentTypeIds: number[];
  order: number;
}

interface UpsertExtractionMetadataDto {
  dataFormat: number;
  extractionQuestion: string;
  displayName: string;
  documentTypeIds: number[];
}

interface ReorderExtractionMetadataDto {
  documentTypeId: number;
  orderedIds: string[];
}

interface TranslateExtractionMetadataDto {
  sourceLanguageId: number;
  sourceText: string;
  targetLanguageIds: number[];
}

interface TranslateExtractionMetadataResponseDto {
  translations: ExtractionMetadataTranslationDto[];
}

interface UpdateExtractionMetadataTranslationsDto {
  translations: ExtractionMetadataTranslationDto[];
}

const metadataDataFormatByApiValue: Record<number, MetadataDataFormat> = {
  1: MetadataDataFormat.Text,
  2: MetadataDataFormat.Date,
  3: MetadataDataFormat.Number,
  4: MetadataDataFormat.YesNo,
  5: MetadataDataFormat.List,
};

const apiValueByMetadataDataFormat: Record<MetadataDataFormat, number> = {
  [MetadataDataFormat.Text]: 1,
  [MetadataDataFormat.Date]: 2,
  [MetadataDataFormat.Number]: 3,
  [MetadataDataFormat.YesNo]: 4,
  [MetadataDataFormat.List]: 5,
};

@Injectable({ providedIn: 'root' })
export class MetadataApiService extends ApiService {
  constructor() {
    super();
  }

  public getMetadata$(documentTypeId?: number): Observable<MetadataField[]> {
    const params =
      documentTypeId !== undefined
        ? new HttpParams().set('documentTypeId', documentTypeId)
        : undefined;

    return this.get<ExtractionMetadataDto[]>('/ExtractionMetadata', params).pipe(
      map((metadata) => metadata.map((item) => this.mapExtractionMetadata(item))),
    );
  }

  public createMetadata$(field: MetadataUpsertModel): Observable<MetadataField> {
    const request: UpsertExtractionMetadataDto = {
      dataFormat: apiValueByMetadataDataFormat[field.format],
      extractionQuestion: field.extractionQuestion,
      displayName: field.displayName,
      documentTypeIds: field.documentTypeIds.map(Number),
    };

    return this.post<ExtractionMetadataDto>('/ExtractionMetadata', request).pipe(
      map((metadata) => this.mapExtractionMetadata(metadata)),
    );
  }

  public updateMetadata$(field: MetadataField): Observable<MetadataField> {
    const request: UpsertExtractionMetadataDto = {
      dataFormat: apiValueByMetadataDataFormat[field.format],
      extractionQuestion: field.extractionQuestion,
      displayName: field.displayName,
      documentTypeIds: field.documentTypeIds.map(Number),
    };

    return this.put<ExtractionMetadataDto>(`/ExtractionMetadata/${field.uniqueId}`, request).pipe(
      map((metadata) => this.mapExtractionMetadata(metadata)),
    );
  }

  public deleteMetadata$(uniqueId: string): Observable<void> {
    return this.delete<void>(`/ExtractionMetadata/${uniqueId}`);
  }

  public reorderMetadata$(documentTypeId: string, orderedIds: string[]): Observable<void> {
    const request: ReorderExtractionMetadataDto = {
      documentTypeId: Number(documentTypeId),
      orderedIds,
    };

    return this.put<void>('/ExtractionMetadata/order', request);
  }

  public translateMetadata$(
    sourceLanguageId: number,
    sourceText: string,
    targetLanguageIds: number[],
  ): Observable<MetadataTranslation[]> {
    const request: TranslateExtractionMetadataDto = {
      sourceLanguageId,
      sourceText,
      targetLanguageIds,
    };

    return this.post<TranslateExtractionMetadataResponseDto>(
      '/ExtractionMetadata/translate',
      request,
    ).pipe(
      map(({ translations }) =>
        (translations || []).map(({ languageId, text }) => ({
          languageId,
          language: '',
          value: text,
        })),
      ),
    );
  }

  public updateMetadataTranslations$(
    uniqueId: string,
    translations: MetadataTranslation[],
  ): Observable<MetadataField> {
    const request: UpdateExtractionMetadataTranslationsDto = {
      translations: translations.map(({ languageId, value }) => ({
        languageId,
        text: value,
      })),
    };

    return this.put<ExtractionMetadataDto>(
      `/ExtractionMetadata/${uniqueId}/translations`,
      request,
    ).pipe(map((metadata) => this.mapExtractionMetadata(metadata)));
  }

  private mapExtractionMetadata(metadata: ExtractionMetadataDto): MetadataField {
    return {
      uniqueId: metadata.id,
      displayName: metadata.displayName,
      format: metadataDataFormatByApiValue[metadata.dataFormat] || MetadataDataFormat.Text,
      documentTypeIds: metadata.documentTypeIds.map((id) => id.toString()),
      documentTypeNames: [],
      extractionQuestion: metadata.extractionQuestion,
      translations: (metadata.translations || []).map(({ languageId, text }) => ({
        languageId,
        language: '',
        value: text,
      })),
      order: metadata.order,
    };
  }
}

export const apiExtractionMetadataProtectedResources = [
  {
    httpMethod: 'GET',
    scopes: [...environment.protectedResources.apiExtractionMetadata.scopes.read],
  },
  {
    httpMethod: 'POST',
    scopes: [...environment.protectedResources.apiExtractionMetadata.scopes.write],
  },
  {
    httpMethod: 'PUT',
    scopes: [...environment.protectedResources.apiExtractionMetadata.scopes.write],
  },
  {
    httpMethod: 'DELETE',
    scopes: [...environment.protectedResources.apiExtractionMetadata.scopes.write],
  },
];
